import React, { useEffect, useState } from 'react';
import { useNotesStore } from '@/stores/agents/notes-store';
import { AgentNote } from '@/services/api/agentApi';
import { FileText, Search, Plus, Pin, Trash2, User, Bot, Share2, Edit3 } from 'lucide-react';

const ownerIcons: Record<string, React.ReactNode> = {
  user: <User className="w-3 h-3" />,
  agent: <Bot className="w-3 h-3" />,
  shared: <Share2 className="w-3 h-3" />,
};

const ownerColors: Record<string, string> = {
  user: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  agent: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  shared: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

export function NotesTab() {
  const { notes, activeNote, isLoading, error, searchQuery, fetchNotes, createNote, updateNote, appendToNote, deleteNote, searchNotes, setActiveNote, setSearchQuery, loadNote } = useNotesStore();
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [appendText, setAppendText] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleSearch = (q: string) => {
    setSearchQuery(q);
    if (q.trim()) {
      searchNotes(q);
    } else {
      fetchNotes();
    }
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    await createNote({ title: newTitle.trim() });
    setNewTitle('');
    setShowCreate(false);
  };

  const handleAppend = async () => {
    if (!activeNote || !appendText.trim()) return;
    await appendToNote(activeNote.note_id, appendText.trim());
    setAppendText('');
  };

  return (
    <div className="flex h-full min-h-[500px]">
      {/* Note list sidebar */}
      <div className="w-80 border-r border-border flex flex-col">
        <div className="p-3 border-b border-border">
          <div className="flex items-center gap-2 mb-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2 w-4 h-4 text-muted-foreground/70" />
              <input
                type="text"
                placeholder="Search notes..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <button
              onClick={() => setShowCreate(!showCreate)}
              className="p-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              aria-label="Create new note"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {showCreate && (
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Note title..."
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                className="flex-1 px-2 py-1 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                autoFocus
              />
              <button onClick={handleCreate} className="px-2 py-1 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90">
                Create
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading && notes.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">Loading...</div>
          ) : notes.length === 0 ? (
            <div className="p-4 text-center">
              <FileText className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">No notes yet</p>
            </div>
          ) : (
            notes.map((note) => (
              <button
                key={note.note_id}
                onClick={() => loadNote(note.note_id)}
                className={`w-full text-left p-3 border-b border-border/50 hover:bg-accent ${activeNote?.note_id === note.note_id ? 'bg-primary/10 border-l-2 border-l-primary' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm text-foreground truncate">{note.title}</span>
                  <div className="flex items-center gap-1">
                    {note.pinned && <Pin className="w-3 h-3 text-yellow-500 dark:text-yellow-400" />}
                    <span className={`flex items-center gap-0.5 px-1.5 py-0.5 text-xs rounded ${ownerColors[note.owner] || 'bg-muted text-muted-foreground'}`}>
                      {ownerIcons[note.owner]}
                    </span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground/70 mt-0.5">
                  {new Date(note.updated_at).toLocaleDateString()}
                  {note.tags.length > 0 && ` \u00B7 ${note.tags.slice(0, 2).join(', ')}`}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Note content area */}
      <div className="flex-1 flex flex-col">
        {activeNote ? (
          <>
            <div className="flex items-center justify-between p-3 border-b border-border">
              <div>
                <h3 className="font-semibold text-foreground">{activeNote.title}</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`flex items-center gap-1 px-1.5 py-0.5 text-xs rounded ${ownerColors[activeNote.owner] || 'bg-muted text-muted-foreground'}`}>
                    {ownerIcons[activeNote.owner]} {activeNote.owner}
                  </span>
                  {activeNote.tags.map(tag => (
                    <span key={tag} className="px-1.5 py-0.5 text-xs bg-muted text-muted-foreground rounded">{tag}</span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {confirmingDelete === activeNote.note_id ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => { deleteNote(activeNote.note_id); setConfirmingDelete(null); }}
                      className="text-xs text-destructive hover:text-destructive/80"
                    >
                      Delete?
                    </button>
                    <button
                      onClick={() => setConfirmingDelete(null)}
                      className="text-xs text-muted-foreground"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmingDelete(activeNote.note_id)}
                    className="p-1.5 text-muted-foreground/70 hover:text-destructive rounded"
                    aria-label={`Delete ${activeNote.title}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <pre className="whitespace-pre-wrap text-sm text-foreground/90 font-sans">{activeNote.content || '(empty note)'}</pre>
            </div>
            <div className="p-3 border-t border-border">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Append text to note..."
                  value={appendText}
                  onChange={(e) => setAppendText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAppend()}
                  className="flex-1 px-3 py-1.5 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                />
                <button onClick={handleAppend} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90">
                  <Edit3 className="w-3 h-3" /> Append
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground/70">
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto mb-2" />
              <p className="text-sm">Select a note to view or edit</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
